import React, { useEffect, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { formatUserLevel } from '@/utils/formatters';
import { patientService } from '@/services/patientService';
import { statisticsService, DashboardStatisticsResponse } from '@/services/statisticsService';
import { Patient } from '@/types/patient.types';
import { ViewPatientDialog } from '@/components/forms/PatientList';
import { AddPatientWizard } from '@/components/forms/AddPatientWizard';
import { cn } from '@/lib/utils';
import {
  Baby,
  FileText,
  Calendar,
  ClipboardList,
  PlusCircle,
  Activity,
  TrendingUp,
  UserPlus,
  Maximize2,
  Minimize2,
  X,
} from 'lucide-react';

export const PrematuriDashboard: React.FC = () => {
  const { utilizator } = useAuth();
  const [dashboardStats, setDashboardStats] = useState<DashboardStatisticsResponse | null>(null);
  const [recentPatients, setRecentPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [isNewPatientModalOpen, setIsNewPatientModalOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  const loadDashboardData = async () => {
    try {
      // Load dashboard statistics for premature patients
      setStatsLoading(true);
      const stats = await statisticsService.getDashboardStatistics('prematuri');
      setDashboardStats(stats);

      // Load recent premature patients
      try {
        const patientsResponse = await patientService.getPatients({
          limit: 5,
          is_premature: true,
        });
        setRecentPatients(
          (patientsResponse.data || []).filter((patient) => Number(patient.is_premature) === 1),
        );
      } catch (error) {
        console.error('Error loading recent premature patients:', error);
        setRecentPatients([]);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setDashboardStats(null);
    } finally {
      setLoading(false);
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    if (utilizator) {
      loadDashboardData();
    }
  }, [utilizator]);

  // Listen for patient created events
  useEffect(() => {
    const handlePatientCreated = () => {
      loadDashboardData();
    };

    window.addEventListener('patient-created', handlePatientCreated);
    return () => {
      window.removeEventListener('patient-created', handlePatientCreated);
    };
  }, []);

  if (!utilizator) {
    return null;
  }

  const stats = [
    {
      title: 'Copii prematuri',
      value: statsLoading ? '...' : (dashboardStats?.patients?.premature_cases || 0).toString(),
      change: '+0',
      icon: Baby,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
    {
      title: 'Cazuri noi (30 zile)',
      value: statsLoading ? '...' : (dashboardStats?.patients?.last_30_days || 0).toString(),
      change: '+0',
      icon: TrendingUp,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      title: 'Completitudine RONN',
      value: statsLoading ? '...' : '85%',
      change: '+5%',
      icon: ClipboardList,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: 'În monitorizare activă',
      value: statsLoading ? '...' : Math.round((dashboardStats?.patients?.premature_cases || 0) * 0.7).toString(),
      change: '+0',
      icon: Activity,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
    },
  ];

  const quickActions = [
    {
      title: 'Pacient nou prematur',
      description: 'Înregistrează un nou pacient prematur',
      icon: PlusCircle,
      action: 'new-premature-case',
      color: 'bg-purple-500',
    },
    {
      title: 'Copii prematuri',
      description: 'Lista pacienților prematuri',
      icon: Baby,
      action: 'premature-patients',
      color: 'bg-blue-500',
    },
  ];

  const handleQuickAction = (action: string) => {
    if (action === 'new-premature-case') {
      // Open the new patient modal directly
      setIsNewPatientModalOpen(true);
    } else {
      const event = new CustomEvent('navigate', { detail: { page: action } });
      window.dispatchEvent(event);
    }
  };

  const handlePatientClick = (patient: Patient) => {
    setSelectedPatient(patient);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedPatient(null);
  };

  return (
    <>
      <MainLayout>
        <div className="space-y-6">
          {/* Welcome Section */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">
                Tablou de bord - Prematuri
              </h1>
              <p className="text-muted-foreground">
                Bine ați venit, {utilizator.name || utilizator.username || 'utilizator'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {utilizator.hospital_name}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="default" className="text-xs bg-purple-500">
                Secțiune prematuri
              </Badge>
              <Badge variant="outline" className="text-xs">
                {utilizator.hospital_name}
              </Badge>
              <Button variant="outline" size="sm">
                <Calendar className="mr-2 h-4 w-4" />
                Astăzi
              </Button>
              <Button size="sm">
                <FileText className="mr-2 h-4 w-4" />
                Raport RONN
              </Button>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4">
            {stats.map((stat) => {
              const Icon = stat.icon;
              return (
                <Card key={stat.title}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      {stat.title}
                    </CardTitle>
                    <div className={`rounded-full p-2 ${stat.bgColor}`}>
                      <Icon className={`h-4 w-4 ${stat.color}`} />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stat.value}</div>
                    {stat.change && (
                      <p className="text-xs text-muted-foreground">
                        <span className="text-green-600">{stat.change}</span> față de luna trecută
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Quick Actions Grid */}
          <div className="w-full">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Acțiuni rapide</h2>
              <p className="text-muted-foreground">Acces rapid la funcționalitățile principale</p>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-4 justify-items-center max-w-4xl mx-auto">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <Card
                    key={action.action}
                    className="cursor-pointer hover:shadow-xl transition-all duration-300 hover:scale-105 hover:-translate-y-1 w-full max-w-xs"
                    onClick={() => handleQuickAction(action.action)}
                  >
                    <CardContent className="p-6 text-center flex flex-col items-center justify-center h-40">
                      <div className={`rounded-xl p-4 ${action.color} text-white shadow-lg mb-4`}>
                        <Icon className="h-8 w-8" />
                      </div>
                      <h3 className="font-bold text-sm mb-2 text-gray-800">{action.title}</h3>
                      <p className="text-xs text-muted-foreground text-center leading-relaxed">
                        {action.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Recent Premature Patients */}
          <div className="grid gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Baby className="h-5 w-5 text-purple-500" />
                    Pacienți prematuri recenți
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleQuickAction('premature-patients')}
                  >
                    Vezi toți
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recentPatients.length > 0 ? (
                  <div className="space-y-4">
                    {recentPatients.map((patient) => (
                      <div
                        key={patient.id}
                        className="flex items-center justify-between p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors cursor-pointer"
                        onClick={() => handlePatientClick(patient)}
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium text-purple-600 hover:text-purple-800">
                            {patient.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Cod: {patient.patient_code} • Născut: {new Date(patient.birth_date).toLocaleDateString('ro-RO')}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {patient.gender === 0 ? '♀ Feminin' : '♂ Masculin'} • Adăugat: {new Date(patient.created_at).toLocaleDateString('ro-RO')}
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge variant="outline" className="text-purple-600 border-purple-300">
                            Prematur
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground text-center py-8">
                    Nu există pacienți prematuri recenți
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* View Patient Dialog */}
        <ViewPatientDialog
          patient={selectedPatient}
          isOpen={viewDialogOpen}
          onClose={handleCloseViewDialog}
        />
      </MainLayout>

      {/* New Premature Patient Modal */}
      <Dialog open={isNewPatientModalOpen} onOpenChange={(open) => {
        setIsNewPatientModalOpen(open);
        if (!open) setIsMaximized(false);
      }}>
        <DialogContent
          hideClose
          className={cn(
            isMaximized
              ? "w-screen h-screen max-w-none max-h-none overflow-y-auto flex flex-col"
              : "w-[90vw] max-w-6xl max-h-[90vh] overflow-y-auto"
          )}
        >
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                <UserPlus className="h-6 w-6" />
                Înregistrare copil prematur
              </DialogTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setIsMaximized(!isMaximized)}
                  className="h-8 w-8"
                >
                  {isMaximized ? (
                    <Minimize2 className="h-4 w-4" />
                  ) : (
                    <Maximize2 className="h-4 w-4" />
                  )}
                </Button>
                <DialogClose asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Închide</span>
                  </Button>
                </DialogClose>
              </div>
            </div>
            <DialogDescription>
              Completați formularul pentru a înregistra un nou copil prematur
            </DialogDescription>
          </DialogHeader>
          <div className={cn("mt-6", isMaximized && "flex-1")}>
            <AddPatientWizard
              onClose={() => setIsNewPatientModalOpen(false)}
              onComplete={(patient) => {
                setIsNewPatientModalOpen(false);
                loadDashboardData();
                window.dispatchEvent(new CustomEvent('patient-created'));
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
